import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';
import type { TemplatedPromptsRepository } from '@src/repositories';
import type { AIPrompt, ClipProducerPreset } from '@src/types.ts/ai_prompts';

export class TemplatedPromptsStore {
  private rootStore: RootStore;
  private readonly _customClipProducerPreset: ClipProducerPreset = {
    title: 'Custom',
    category: 'clip_producer_custom',
    userPromptDescription: '',
  };
  private _clipProducerPresets: ClipProducerPreset[] = [];
  private _aiBlogTemplates: AIPrompt[] = [];
  private _aiSocialPostTemplates: AIPrompt[] = [];
  private _aiEmailTemplates: AIPrompt[] = [];
  private _aiShareableImagesTemplates: AIPrompt[] = [];
  private _aiTalkingPointsTemplate: AIPrompt | null = null;
  private _aiImpactScoreTemplate: AIPrompt | null = null;
  private _graphicCategories: string[] = [];
  private _graphicSubcategories: string[] = [];
  private _videoTemplates: string[] = [];
  private _isInitialized: boolean = false;

  private get repository(): TemplatedPromptsRepository {
    if (!this.rootStore.datoClientStore) {
      throw new Error('Templated prompts repository not initialized');
    }
    return this.rootStore.datoClientStore.templatedPromptsRepository;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get customClipProducerPreset(): ClipProducerPreset {
    return this._customClipProducerPreset;
  }

  get clipProducerPresets(): ClipProducerPreset[] {
    return this._clipProducerPresets;
  }

  get aiBlogTemplates(): AIPrompt[] {
    return this._aiBlogTemplates;
  }

  get aiSocialPostTemplates(): AIPrompt[] {
    return this._aiSocialPostTemplates;
  }

  get aiEmailTemplates(): AIPrompt[] {
    return this._aiEmailTemplates;
  }

  get aiShareableImagesTemplates(): AIPrompt[] {
    return this._aiShareableImagesTemplates;
  }

  get aiTalkingPointsTemplate(): AIPrompt | null {
    return this._aiTalkingPointsTemplate;
  }

  get aiImpactScoreTemplate(): AIPrompt | null {
    return this._aiImpactScoreTemplate;
  }

  get graphicCategoriesIds(): string[] {
    return this._graphicCategories;
  }

  get graphicSubcategoriesIds(): string[] {
    return this._graphicSubcategories;
  }

  get videoTemplatesIds(): string[] {
    return this._videoTemplates;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  private setClipProducerPresets(presets: ClipProducerPreset[]) {
    this._clipProducerPresets = presets;
  }

  private setAiBlogTemplates(templates: AIPrompt[]) {
    this._aiBlogTemplates = templates;
  }

  private setAiSocialPostTemplates(templates: AIPrompt[]) {
    this._aiSocialPostTemplates = templates;
  }

  private setAiEmailTemplates(templates: AIPrompt[]) {
    this._aiEmailTemplates = templates;
  }

  private setAiShareableImagesTemplates(templates: AIPrompt[]) {
    this._aiShareableImagesTemplates = templates;
  }

  private setAiTalkingPointsTemplate(template: AIPrompt | null) {
    this._aiTalkingPointsTemplate = template;
  }

  private setAiImpactScoreTemplate(template: AIPrompt | null) {
    this._aiImpactScoreTemplate = template;
  }

  private setGraphicCategories(categories: string[]) {
    this._graphicCategories = categories;
  }

  private setGraphicSubcategories(subcategories: string[]) {
    this._graphicSubcategories = subcategories;
  }

  private setVideoTemplates(templates: string[]) {
    this._videoTemplates = templates;
  }

  private setIsInitialized(value: boolean) {
    this._isInitialized = value;
  }

  async initTemplatedPrompts(storyId: string) {
    this.setIsInitialized(false);
    try {
      const {
        clipProducerPresets,
        aiBlogTemplates,
        aiSocialPostTemplates,
        aiEmailTemplates,
        aiShareableImagesTemplates,
        aiTalkingPointsTemplate,
        aiImpactScoreTemplate,
        graphicCategories,
        graphicSubcategories,
        videoTemplates,
      } = await this.repository.fetchTemplatedPrompts(storyId);

      this.setClipProducerPresets(clipProducerPresets);
      this.setAiBlogTemplates(aiBlogTemplates);
      this.setAiSocialPostTemplates(aiSocialPostTemplates);
      this.setAiEmailTemplates(aiEmailTemplates);
      this.setAiShareableImagesTemplates(aiShareableImagesTemplates);
      this.setAiTalkingPointsTemplate(aiTalkingPointsTemplate);
      this.setAiImpactScoreTemplate(aiImpactScoreTemplate);
      this.setGraphicCategories(graphicCategories);
      this.setGraphicSubcategories(graphicSubcategories);
      this.setVideoTemplates(videoTemplates);
    } finally {
      this.setIsInitialized(true);
    }
  }
}
